<template>
  <div v-if="oneTimeMessages.length > 0 || persistentMessages.length > 0" class="user-messages-container">
    <!-- Persistent Messages Carousel (single rotating message) -->
    <div 
      v-if="currentPersistentMessage && !firstOneTimeMessage"
      :class="['message-banner', messageLevelClass(currentPersistentMessage.messageLevel), { 'minimized-position': isMinimized }]"
      :style="{ top: persistentMessageTop }"
      :key="currentPersistentMessage.id"
    >
      <!-- Window Close Button (top-right) -->
      <button 
        type="button"
        class="banner-close-btn" 
        @click="togglePersistentPosition"
        :title="isMinimized ? $t('common.expand') : $t('common.close')"
      >
        <i :class="isMinimized ? 'ph-duotone ph-arrows-out' : 'ti ti-x'"></i>
      </button>

      <div class="message-content">
        <i :class="['message-icon', messageLevelIcon(currentPersistentMessage.messageLevel)]"></i>
        <div class="message-text">
          <strong class="message-title">{{ currentPersistentMessage.title }}</strong>
          <p class="message-body">{{ currentPersistentMessage.content }}</p>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div v-if="persistentMessages.length > 1" class="message-controls">
        <button 
          type="button"
          class="nav-btn" 
          @click="previousMessage"
          :title="$t('common.previous')"
        >
          <i class="ph-duotone ph-caret-left"></i>
        </button>
        <button 
          type="button"
          class="nav-btn" 
          @click="nextMessage"
          :title="$t('common.next')"
        >
          <i class="ph-duotone ph-caret-right"></i>
        </button>
      </div>
    </div>

    <!-- OneTime modal-style overlay -->
    <div
      v-if="firstOneTimeMessage"
      class="one-time-overlay"
    ></div>

    <!-- OneTime Message (centered, SweetAlert-like, shown one at a time) -->
    <div 
      v-if="firstOneTimeMessage" 
      :key="firstOneTimeMessage.id" 
      :class="['message-banner', 'one-time-banner', messageLevelClass(firstOneTimeMessage.messageLevel)]"
    >
      <div class="message-content">
        <i :class="['message-icon', messageLevelIcon(firstOneTimeMessage.messageLevel)]"></i>
        <div class="message-text">
          <strong class="message-title">{{ firstOneTimeMessage.title }}</strong>
          <p class="message-body">{{ firstOneTimeMessage.content }}</p>
        </div>
      </div>
      
      <button 
        type="button"
        class="btn-accept" 
        @click="acceptMessage(firstOneTimeMessage)"
        :disabled="acceptingMessage === firstOneTimeMessage.id"
      >
        <i v-if="acceptingMessage === firstOneTimeMessage.id" class="ph-duotone ph-spinner spinner-icon"></i>
        <span v-else>{{ $t('userMessages.accept') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import userMessageService from '@/services/userMessageService';

const store = useStore();
const { t } = useI18n();

// State
const messages = ref([]);
const acceptingMessage = ref(null);
const refreshInterval = ref(null);
const currentIndex = ref(0);
const rotationInterval = ref(null);
const isMinimized = ref(false);

// Computed
const currentUser = computed(() => store.getters['auth/currentUser']);
const isImpersonating = computed(() => currentUser.value?.isImpersonating === true);

// Current persistent message to display in carousel
const currentPersistentMessage = computed(() => {
  if (persistentMessages.value.length === 0) return null;
  return persistentMessages.value[currentIndex.value] || persistentMessages.value[0];
});

// First OneTime message to display (single, centered)
const firstOneTimeMessage = computed(() => {
  return oneTimeMessages.value.length > 0 ? oneTimeMessages.value[0] : null;
});

// Calculate base top position for persistent carousel
const baseTop = computed(() => {
  const base = isImpersonating.value ? 60 : 12;
  return `${base}px`;
});

// Calculate top position for persistent message (changes based on minimized state)
const persistentMessageTop = computed(() => {
  if (isMinimized.value) {
    // When minimized, use 'bottom' positioning instead
    return 'auto';
  }
  return baseTop.value;
});

// Get accepted messages from localStorage
const getAcceptedMessages = () => {
  try {
    const userId = currentUser.value?.id;
    if (!userId) return [];
    
    const key = `accepted-messages-${userId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading accepted messages from localStorage:', error);
    return [];
  }
};

// Save accepted message to localStorage
const saveAcceptedMessage = (messageId) => {
  try {
    const userId = currentUser.value?.id;
    if (!userId) return;
    
    const key = `accepted-messages-${userId}`;
    const accepted = getAcceptedMessages();
    
    if (!accepted.includes(messageId)) {
      accepted.push(messageId);
      localStorage.setItem(key, JSON.stringify(accepted));
    }
  } catch (error) {
    console.error('Error saving accepted message to localStorage:', error);
  }
};

// Filter and sort messages
// Filter and sort messages - separate OneTime and Persistent
const oneTimeMessages = computed(() => {
  const acceptedMessageIds = getAcceptedMessages();
  
  return messages.value
    .filter(message => {
      // MessageType: 1 = OneTime
      return message.messageType === 1 && !acceptedMessageIds.includes(message.id);
    })
    .sort((a, b) => {
      // Sort by priority (descending) then by startDate (ascending)
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return new Date(a.startDate) - new Date(b.startDate);
    });
});

const persistentMessages = computed(() => {
  return messages.value
    .filter(message => {
      // MessageType: 2 = Persistent
      return message.messageType === 2;
    })
    .sort((a, b) => {
      // Sort by priority (descending) then by startDate (ascending)
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return new Date(a.startDate) - new Date(b.startDate);
    });
});

// Get message level class
const messageLevelClass = (level) => {
  // MessageLevel: 1 = Info, 2 = Warning, 3 = Critical
  switch (level) {
    case 1:
      return 'info';
    case 2:
      return 'warning';
    case 3:
      return 'critical';
    default:
      return 'info';
  }
};

// Get message level icon
const messageLevelIcon = (level) => {
  // MessageLevel: 1 = Info, 2 = Warning, 3 = Critical
  switch (level) {
    case 1:
      return 'ph-duotone ph-info';
    case 2:
      return 'ph-duotone ph-warning';
    case 3:
      return 'ph-duotone ph-warning-octagon';
    default:
      return 'ph-duotone ph-info';
  }
};

// Navigation functions for persistent messages
const nextMessage = () => {
  if (persistentMessages.value.length <= 1) return;
  currentIndex.value = (currentIndex.value + 1) % persistentMessages.value.length;
  resetRotationInterval();
};

const previousMessage = () => {
  if (persistentMessages.value.length <= 1) return;
  currentIndex.value = (currentIndex.value - 1 + persistentMessages.value.length) % persistentMessages.value.length;
  resetRotationInterval();
};

// Start auto-rotation for persistent messages
const startRotation = () => {
  if (rotationInterval.value) {
    clearInterval(rotationInterval.value);
  }
  
  // Only rotate if there are multiple persistent messages
  if (persistentMessages.value.length > 1) {
    rotationInterval.value = setInterval(() => {
      nextMessage();
    }, 5000); // Rotate every 5 seconds
  }
};

// Reset rotation interval (when user manually navigates)
const resetRotationInterval = () => {
  startRotation();
};

// Toggle persistent messages position (center <-> left side)
const togglePersistentPosition = () => {
  isMinimized.value = !isMinimized.value;
  // Rotation continues in both positions
};

// Load messages from API
const loadMessages = async () => {
  try {
    const response = await userMessageService.getActiveMessages();
    messages.value = Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error loading user messages:', error);
    // Silently fail - don't show error to user
  }
};

// Accept message
const acceptMessage = async (message) => {
  if (acceptingMessage.value) return;
  
  acceptingMessage.value = message.id;
  
  try {
    await userMessageService.acceptMessage(message.id);
    
    // For OneTime messages, save to localStorage
    if (message.messageType === 1) {
      saveAcceptedMessage(message.id);
    }
    
    // Show success notification
    store.dispatch('notify', {
      message: t('userMessages.acknowledged'),
      type: 'success'
    });
    
    // Reload messages to update display
    await loadMessages();
  } catch (error) {
    console.error('Error accepting message:', error);
    store.dispatch('notify', {
      message: t('common.errors.generic'),
      type: 'error'
    });
  } finally {
    acceptingMessage.value = null;
  }
};

// Watch for persistent messages changes
watch(() => persistentMessages.value.length, (newLength, oldLength) => {
  // Reset index if it's out of bounds
  if (currentIndex.value >= newLength) {
    currentIndex.value = Math.max(0, newLength - 1);
  }
  // Restart rotation when messages change
  startRotation();
});

// Lifecycle hooks
onMounted(async () => {
  await loadMessages();
  startRotation();
  
  // Refresh messages every 10 minutes
  refreshInterval.value = setInterval(async () => {
    await loadMessages();
    currentIndex.value = 0; // Reset to first message after reload
    startRotation();
  }, 10 * 60 * 1000);
});

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
  if (rotationInterval.value) {
    clearInterval(rotationInterval.value);
  }
});
</script>

<style scoped>
.user-messages-container {
  position: fixed;
  z-index: 4002;
  width: 100%;
  pointer-events: none;
}

.message-banner {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4002;
  border-radius: 6px;
  padding: 0.65rem 2rem 0.65rem 1rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  min-width: 480px;
  max-width: 2000px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.875rem;
  animation: fadeIn 0.3s ease-out;
  pointer-events: auto;
  transition: all 0.4s ease-in-out;
}

.message-banner.one-time-banner {
  top: 50%;
  transform: translate(-50%, -50%);
  max-width: 720px;
  width: min(90%, 720px);
  padding: 1.5rem 2rem;
  border-radius: 12px;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  text-align: center;
}

.one-time-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 4001;
  pointer-events: auto;
}

.one-time-banner .message-content {
  flex-direction: column;
  align-items: center;
}

.one-time-banner .message-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.one-time-banner .message-title {
  font-size: 1.15rem;
}

.one-time-banner .message-body {
  font-size: 0.9rem;
  max-height: 260px;
  overflow-y: auto;
  text-align: left;
}

.one-time-banner .btn-accept {
  align-self: center;
  font-size: 0.9rem;
  padding: 0.45rem 1.2rem;
}

.message-banner.minimized-position {
  left: 8px;
  bottom: 20px;
  top: auto;
  transform: translateX(0) translateY(0);
  /* Keep the same horizontal layout, no size/shape changes */
}

.message-banner.minimized-position .message-body {
  white-space: normal;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.message-controls {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  margin-left: auto;
  padding-left: 0.75rem;
}

.nav-btn {
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: inherit;
  cursor: pointer;
  padding: 0.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  font-size: 1rem;
  width: 32px;
  height: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.nav-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.banner-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: inherit;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.banner-close-btn:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.message-banner.info {
  background: #d1ecf1;
  border-left: 4px solid #0c5460;
  color: #0c5460;
}

.message-banner.warning {
  background: #fff3cd;
  border-left: 4px solid #856404;
  color: #856404;
}

.message-banner.critical {
  background: #f8d7da;
  border-left: 4px solid #721c24;
  color: #721c24;
}

.message-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.message-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.message-text {
  flex: 1;
  min-width: 0;
}

.message-title {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.message-body {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.35;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-line;
}

.btn-accept {
  background: transparent;
  border: 1px solid currentColor;
  color: inherit;
  padding: 0.3rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-accept:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.08);
  border-color: currentColor;
  transform: translateY(-1px);
}

.btn-accept:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .message-banner:not(.minimized-position) {
    left: 1rem;
    right: 1rem;
    transform: translateX(0);
    min-width: auto;
    max-width: none;
    width: calc(100% - 2rem);
  }
  
  .message-banner.minimized-position {
    left: 0;
    bottom: 10px;
    max-width: 100%;
    min-width: 100%;
  }
  
  .nav-arrow {
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .message-banner {
    flex-direction: column;
    align-items: flex-start;
    padding: 0.75rem;
  }
  
  .btn-accept {
    align-self: flex-end;
    width: 100%;
    justify-content: center;
  }
}
</style>
